import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  CircularProgress, ThemeProvider, Typography, Box,
} from '@material-ui/core';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY, ACCESS_CODE_SESSION_KEY, PRODUCT_DEEP_LINKS } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY } from '@/apollo/queries';
import { ACTIVATE_USER_BY_ACCESS_CODE } from '@/apollo/mutations';

/**
 * Extract entitlement status from a Supabase JWT.
 * Returns 'active', 'pending', or undefined if not present.
 */
const getEntitlementStatus = (accessToken: string): string | undefined => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return payload.bh_claims?.products?.[0]?.status;
  } catch {
    return undefined;
  }
};

// TODO: Remove debug UI before merging
const DebugUI: React.FC<{ logs: string[] }> = ({ logs }) => (
  <Box p={2} style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>
    <Typography variant="h6">EmailAuthCallback Debug</Typography>
    {logs.map((log) => (
      <div key={log} style={{ marginTop: 4, color: log.includes('ERROR') ? 'red' : 'inherit' }}>
        {log}
      </div>
    ))}
    <CircularProgress size={20} style={{ marginTop: 16 }} />
  </Box>
);

const EmailAuthCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });
  const [activateUserByAccessCode] = useMutation(ACTIVATE_USER_BY_ACCESS_CODE);
  // TODO: Remove debug state
  const [debugLogs, setDebugLogs] = useState<string[]>(['Starting callback...']);
  const addLog = (msg: string) => setDebugLogs((prev) => [...prev, msg]);

  useEffect(() => {
    // Login flow: user already active — skip activation, go straight to session handoff.
    // Same pattern as SmsVerify's fromLogin path.
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        console.error('[EmailAuthCallback] refreshSession failed:', refreshError?.message);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY)
        || PRODUCT_DEEP_LINKS[product];
      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        console.error('[EmailAuthCallback] redirectTo is missing or invalid');
        history.replace('/spark/login');
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        console.error('[EmailAuthCallback] requestAuthCode failed:', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: {
          headers: { Authorization: `Bearer ${refreshedSession.access_token}` },
        },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        addLog('Waiting 1.5s for Supabase hash processing...');
        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        addLog('Calling getSession...');
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        addLog(`Session: ${session ? 'YES' : 'NO'}`);

        if (!session) {
          addLog('ERROR: No session — redirecting to access-code');
          history.replace(`/activate/${product}/access-code`, {
            error: 'email_verification_failed',
          });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        addLog(`supabaseUserId: ${supabaseUserId.substring(0, 8)}...`);
        addLog(`patientUid: ${patientUid.substring(0, 8)}...`);

        const entitlementStatus = getEntitlementStatus(session.access_token);
        const accessCode = session.user?.user_metadata?.accessCode || null;
        const hasAccessCodeInSessionStorage = !!sessionStorage.getItem(ACCESS_CODE_SESSION_KEY);

        addLog(`entitlementStatus: ${entitlementStatus || 'undefined'}`);
        addLog(`accessCode from metadata: ${accessCode || 'NOT FOUND'}`);
        addLog(`accessCode in sessionStorage: ${hasAccessCodeInSessionStorage}`);

        // Determine flow: if access code is present (metadata or sessionStorage),
        // this is an activation flow even if entitlement is already active.
        // Login flow only when there's no access code AND status is active.
        const isActivationFlow = !!accessCode || hasAccessCodeInSessionStorage;
        const isReturningUser = !isActivationFlow && entitlementStatus === 'active';
        addLog(`isActivationFlow: ${isActivationFlow}`);
        addLog(`isReturningUser: ${isReturningUser}`);

        if (isReturningUser) {
          addLog('Going to login handoff flow...');
          await handleLoginFlow();
          return;
        }

        // Activation flow: new user — confirm entitlement and activate in Cortex.
        addLog(`user_metadata: ${JSON.stringify(session.user?.user_metadata)}`);

        try {
          addLog('calling activateSparkConfirm...');
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
          addLog('activateSparkConfirm: SUCCESS');

          if (accessCode) {
            addLog(`calling activateUserByAccessCode(${accessCode})...`);
            const result = await activateUserByAccessCode({
              variables: { accessCode },
              context: { headers: { Authorization: `Bearer ${session.access_token}` } },
            });
            const success = result.data?.activateUserByAccessCode?.success;
            addLog(`activateUserByAccessCode: ${success ? 'SUCCESS' : `FAILED (success=${success})`}`);
          } else {
            addLog('skipping activateUserByAccessCode — no access code');
          }
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          addLog(`ERROR activation failed: ${errMsg}`);
          console.error('[EmailAuthCallback] activation failed:', err);
        } finally {
          sessionStorage.removeItem(ACCESS_CODE_SESSION_KEY);
        }

        // Store redirect target for UserInfoConfirmation session handoff
        const redirectTo = PRODUCT_DEEP_LINKS[product];
        if (redirectTo && isSafeRedirectUri(redirectTo)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
        }

        history.replace(`/activate/${product}/user-info/confirmation`, {
          patientUid,
          flow: 'email',
        });
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : String(err);
        addLog(`ERROR unexpected: ${errMsg}`);
        console.error('[EmailAuthCallback] Unexpected error:', err);
        // NOT redirecting so you can read the error
      }
    };

    handleCallback();
  }, [history, product, fetchMe, activateUserByAccessCode]);

  // TODO: Remove debug UI — change to spinner before merging
  return (
    <ThemeProvider theme={getTheme(product)}>
      <DebugUI logs={debugLogs} />
    </ThemeProvider>
  );
};

export default EmailAuthCallback;
